import type { IMetadata } from '@adloid-technologies/discover-sdk';

/**
 * Cadillac Escalade (linkId 2383, project 59).
 *
 * Converted from the 1.0 repo's `src/metadata/data/metadata-2383.ts`
 * (`appData.visualiser.models[0]`, array-shaped) into the 2.0 object-keyed
 * `IMetadata` shape, following the conventions established for 2382/2377:
 * consolidate-or-split fingerprinting per entity, `autoIntelRPOCode` from
 * `brandItemCode`, real condition/isDefault/isStop threading on accessory
 * messages, model-level `hotspots` built from the default variant, and
 * `groupId`/`isDefault` derived from `removeAccessories` via connected
 * components (with single-select group categories normalised to the group's
 * dominant category — 1.0 sometimes mislabels one member).
 *
 * Real accessory-grouping bug found and fixed while building this
 * generator (gmc-lib.mjs deriveGroups): Luxury and Sport both have a
 * hidden `default_speaker` accessory excluded by TWO otherwise-unrelated
 * toggles — an audio-system upgrade AND a separate night-vision on/off
 * pair — that don't reference each other. Naive connected-components
 * grouping (correct for every genuine N-way clique found so far, e.g.
 * Yukon's 6-way alloy-wheel set) would have merged these into one wrong
 * 4-way group. Fixed generally: a hub is only treated as one real
 * multi-way group if its neighbours are mutually reachable without it
 * (a clique/chain); otherwise only its first (lowest-key) edge is kept
 * and the rest — Night Vision here — form their own separate group.
 * Verified: {default_speaker, Audio System} and {WO_NIGHT_VISION, Night
 * Vision} now correctly land as two independent groups on both trims.
 *
 * Generated — see the conversion notes in ui-metadata.ts.
 */
export const metadata: IMetadata = {
  models: {
    Escalade: {
      id: 'Escalade',
      lightingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      postProcessingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      backgroundIdentifer: 'dome',
      variants: {
        luxury: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6251',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6259',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6418',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '6271',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        sport: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6251',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6259',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6418',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '6271',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        'platinum-luxury': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6251',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6259',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6405',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '6271',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        'platinum-sport': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6251',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6259',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6405',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '6271',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        'v-series': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6251',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6259',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6595',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '6271',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          isDefault: false,
          sku: '6272',
          project: '59',
        },
        g0ZA968_RpUe: {
          isDefault: true,
          sku: '6271',
          project: '59',
        },
      },
      ambientLighting: {
        ambientColors: '#ff1300,#0040FF,#ff9f51,#aafef4,#00b6ff,#61ff87',
        ambientLightings: {
          ambientRed: {
            isDefault: true,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              sport: 'interior-color',
              'platinum-luxury': 'interior-color',
              'platinum-sport': 'interior-color',
              'v-series': 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_01_GLOW',
              'INT_AMBIENT_02_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [255, 19, 0],
            emissiveIntensity: 0.04,
          },
          ambientBlue: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              sport: 'interior-color',
              'platinum-luxury': 'interior-color',
              'platinum-sport': 'interior-color',
              'v-series': 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_01_GLOW',
              'INT_AMBIENT_02_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [0, 64, 255],
            emissiveIntensity: 0.04,
          },
          ambientOrange: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              sport: 'interior-color',
              'platinum-luxury': 'interior-color',
              'platinum-sport': 'interior-color',
              'v-series': 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_01_GLOW',
              'INT_AMBIENT_02_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [255, 159, 81],
            emissiveIntensity: 0.04,
          },
          ambientCyan: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              sport: 'interior-color',
              'platinum-luxury': 'interior-color',
              'platinum-sport': 'interior-color',
              'v-series': 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_01_GLOW',
              'INT_AMBIENT_02_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [170, 254, 244],
            emissiveIntensity: 0.04,
          },
          ambientSkyBlue: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              sport: 'interior-color',
              'platinum-luxury': 'interior-color',
              'platinum-sport': 'interior-color',
              'v-series': 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_01_GLOW',
              'INT_AMBIENT_02_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [0, 182, 255],
            emissiveIntensity: 0.04,
          },
          ambientGreen: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              sport: 'interior-color',
              'platinum-luxury': 'interior-color',
              'platinum-sport': 'interior-color',
              'v-series': 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_01_GLOW',
              'INT_AMBIENT_02_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [97, 255, 135],
            emissiveIntensity: 0.04,
          },
        },
        relevantVariants: ['luxury', 'sport', 'platinum-luxury', 'platinum-sport', 'v-series'],
      },
      colors: {
        exterior: {
          '30ja6fs1si26': {
            sku: '6256',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          '1d0y9evbmngc': {
            sku: '6259',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          mwsv1fnhg5wz: {
            sku: '6260',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          onwrm9wz90ia: {
            sku: '6257',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          wqo8160kpmns: {
            sku: '6254',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          bpsnuu0maoxi: {
            sku: '6255',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          w2x9zru8b9md: {
            sku: '6256',
            project: '59',
            relevantVariants: [
              'sport',
            ],
          },
          oo9of93p42zt: {
            sku: '6259',
            project: '59',
            relevantVariants: [
              'sport',
            ],
          },
          mkosdhfocbor: {
            sku: '6260',
            project: '59',
            relevantVariants: [
              'sport',
            ],
          },
          woq5tg300942: {
            sku: '6257',
            project: '59',
            relevantVariants: [
              'sport',
            ],
          },
          m7whgsqs0reu: {
            sku: '6254',
            project: '59',
            relevantVariants: [
              'sport',
            ],
          },
          bpsnuopu0maoxi: {
            sku: '6255',
            project: '59',
            relevantVariants: [
              'sport',
            ],
          },
          '1x3l0pzugt5o': {
            sku: '6256',
            project: '59',
            relevantVariants: [
              'platinum-luxury',
            ],
          },
          de8yhyoz7ilw: {
            sku: '6259',
            project: '59',
            relevantVariants: [
              'platinum-luxury',
            ],
          },
          n8kjkv3l71rj: {
            sku: '6260',
            project: '59',
            relevantVariants: [
              'platinum-luxury',
            ],
          },
          umw4uekyrr6c: {
            sku: '6257',
            project: '59',
            relevantVariants: [
              'platinum-luxury',
            ],
          },
          '48k36mijbi84': {
            sku: '6254',
            project: '59',
            relevantVariants: [
              'platinum-luxury',
            ],
          },
          lczhp0lkw9x5: {
            sku: '6256',
            project: '59',
            relevantVariants: [
              'platinum-sport',
            ],
          },
          ean8osok96bg: {
            sku: '6259',
            project: '59',
            relevantVariants: [
              'platinum-sport',
            ],
          },
          '4nkqa7bbmps8': {
            sku: '6260',
            project: '59',
            relevantVariants: [
              'platinum-sport',
            ],
          },
          uqziwot9305o: {
            sku: '6257',
            project: '59',
            relevantVariants: [
              'platinum-sport',
            ],
          },
          hrm6fvne1q87: {
            sku: '6254',
            project: '59',
            relevantVariants: [
              'platinum-sport',
            ],
          },
          imj5i4p0mwho: {
            sku: '6256',
            project: '59',
            relevantVariants: [
              'v-series',
            ],
          },
          '9zzwwce8hhvm': {
            sku: '6259',
            project: '59',
            relevantVariants: [
              'v-series',
            ],
          },
          m1n06yw87mjc: {
            sku: '6260',
            project: '59',
            relevantVariants: [
              'v-series',
            ],
          },
          z10htvrc4695: {
            sku: '6257',
            project: '59',
            relevantVariants: [
              'v-series',
            ],
          },
          t0l2tlj9fv25: {
            sku: '6255',
            project: '59',
            relevantVariants: [
              'v-series',
            ],
          },
        },
        interior: {
          '03wk11dwf99s': {
            sku: '6418',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['8yrplilfy10w5s', '8yrplinhllfy10w5s'],
          },
          j50tistnihpo: {
            sku: '6415',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['8yrplilfy10w5s', '8yrplinhllfy10w5s'],
          },
          j50tistnihp1: {
            sku: '6411',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['8yrplilfy10w5s', '8yrplinhllfy10w5s'],
          },
          '85nvtwjjtuim': {
            sku: '6418',
            project: '59',
            relevantVariants: [
              'sport',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['12a5c5qeaf6c', '6yw6n1ws9p68'],
          },
          ww5be0hikbdt: {
            sku: '6427',
            project: '59',
            relevantVariants: [
              'sport',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['12a5c5qeaf6c', '6yw6n1ws9p68'],
          },
          vvk04t5qc25i: {
            sku: '6411',
            project: '59',
            relevantVariants: [
              'sport',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['12a5c5qeaf6c', '6yw6n1ws9p68'],
          },
          h0cuksv2pqic: {
            sku: '6405',
            project: '59',
            relevantVariants: [
              'platinum-luxury',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['lrhbknsfx974', 'gfd5nv4niood'],
          },
          x6jha35ah86k: {
            sku: '6419',
            project: '59',
            relevantVariants: [
              'platinum-luxury',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['lrhbknsfx974', 'gfd5nv4niood'],
          },
          do54iqbddf9v: {
            sku: '6404',
            project: '59',
            relevantVariants: [
              'platinum-luxury',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['lrhbknsfx974', 'gfd5nv4niood'],
          },
          w2cu8xd8vbzl: {
            sku: '6405',
            project: '59',
            relevantVariants: [
              'platinum-sport',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['w2tf0r4a2knz', 'fiu9a1c8ir5n'],
          },
          nl6ui3aic9te: {
            sku: '6419',
            project: '59',
            relevantVariants: [
              'platinum-sport',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['w2tf0r4a2knz', 'fiu9a1c8ir5n'],
          },
          '7s7c7qz71764': {
            sku: '6404',
            project: '59',
            relevantVariants: [
              'platinum-sport',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['w2tf0r4a2knz', 'fiu9a1c8ir5n'],
          },
          pcy4nlxr756u: {
            sku: '6595',
            project: '59',
            relevantVariants: [
              'v-series',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['2gxjds87y443'],
          },
          ys9drkq7y7ju: {
            sku: '6594',
            project: '59',
            relevantVariants: [
              'v-series',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['2gxjds87y443'],
          },
        },
      },
      features: {
        '50fyijsa8h88': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Exterior_Design',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '1e0q63itjg52': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Suspension',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        x5p4553jsccd: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Slip_Differential',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        x5p450953jsccd: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'SHADER_Illuminated_Logo',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Illuminated_Logo',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        sdkeibj4zedx: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'SHADER_Infotainment_screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment_Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '9v7g6wt62yu6': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Command_Center',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Command_Center',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        g6h9z7ho9usu: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Ambient_Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ja33evostgzo4: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Super_Cruise',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Super_Cruise',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ja35evostgzo4: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Headup_Display',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Headup_Display',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        q7lhy68om5cb: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'Exterior_Design',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '6ysctwifu1yb': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'SHADER_Sport_grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Sport_Grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        cu0f7ese0i1o: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'Suspension',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '8ptzflr2c84c': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'Slip_Differential',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '2gn5w9aahsu1': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'SHADER_Illuminated_Logo',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Illuminated_Logo',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        hpj0bzml4qf6: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'SHADER_Infotainment_screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment_Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        knb6d6ov609e: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'SHADER_Sound_System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Sound_System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          // DOORS_CLOSE used to be baked into `messages` above, but every
          // waitForFulfill:false message (this one and the shader) fires in
          // the same non-awaited batch - so the shader started playing at the
          // exact same instant the door was told to close, rendering against
          // an open-door pose for the first moments of the door's closing
          // animation. affectedInteractionOnApply is awaited before `messages`
          // starts (see HandleFeaturesAutoplay), so the door-close is properly
          // sequenced ahead of the shader instead of racing it.
          affectedInteractionOnApply: {
            '8xaasoxlyubu': { add: false, snapToLastFrame: true },
          },
        },
        e097g5qkjv2c: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'Command_Center',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Command_Center',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        hfv7nd2hrk5v: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'Ambient_Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        irh0u7svt9w8: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'Super_Cruise',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Super_Cruise',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '2vl8wmotu9jo': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'Headup_Display',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Headup_Display',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        biwfr75k1usd: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'platinum-luxury',
          ],
          messages: [
            {
              message: 'Exterior_Design',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '0y6yp2hp5d52': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'platinum-luxury',
          ],
          messages: [
            {
              message: 'Suspension',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        wd5y4twqd1ig: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'platinum-luxury',
          ],
          messages: [
            {
              message: 'Slip_Differential',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        dy5tr3bmtzeg: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'platinum-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Illuminated_Logo',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Illuminated_Logo',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        tq0lmsblvr5q: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Infotainment_screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment_Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        z6tytr3ven4z: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Sound_System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Sound_System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          // See knb6d6ov609e (Sport)'s comment - DOORS_CLOSE was racing the
          // shader in the same non-awaited message batch. Moved to
          // affectedInteractionOnApply, which is awaited before `messages`
          // starts, so the door finishes closing first.
          affectedInteractionOnApply: {
            '6gohvikpcnsq': { add: false, snapToLastFrame: true },
          },
        },
        kbnsp4u7me10: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum-luxury',
          ],
          messages: [
            {
              message: 'Command_Center',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Command_Center',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        u3css7vd0vwi: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum-luxury',
          ],
          messages: [
            {
              message: 'Ambient_Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        kxjqj2jvqqtx: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum-luxury',
          ],
          messages: [
            {
              message: 'Entertainment_System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_ Entertainment_System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        tzrpfdz82u9h: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum-luxury',
          ],
          messages: [
            {
              message: 'Super_Cruise',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Super_Cruise',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        cf97wd6fshm9: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum-luxury',
          ],
          messages: [
            {
              message: 'Headup_Display',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Headup_Display',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '26yhehm42yz1': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'platinum-sport',
          ],
          messages: [
            {
              message: 'Exterior_Design',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        pjcx8ghbj12t: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'platinum-sport',
          ],
          messages: [
            {
              message: 'SHADER_Sport_grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Sport_Grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ppv43v19fgn2: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'platinum-sport',
          ],
          messages: [
            {
              message: 'Suspension',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        m5ueop90f4sh: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'platinum-sport',
          ],
          messages: [
            {
              message: 'Slip_Differential',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        hihm6d5nyshp: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'platinum-sport',
          ],
          messages: [
            {
              message: 'SHADER_Illuminated_Logo',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Illuminated_Logo',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        fwiqhrtu5nm1: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum-sport',
          ],
          messages: [
            {
              message: 'SHADER_Infotainment_screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment_Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        lg9x6hx64lar: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum-sport',
          ],
          messages: [
            {
              message: 'SHADER_Sound_System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Sound_System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          // See knb6d6ov609e (Sport)'s comment - DOORS_CLOSE was racing the
          // shader in the same non-awaited message batch. Moved to
          // affectedInteractionOnApply, which is awaited before `messages`
          // starts, so the door finishes closing first.
          affectedInteractionOnApply: {
            sl5w76z5a23v: { add: false, snapToLastFrame: true },
          },
        },
        '21ms0e46yfck': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum-sport',
          ],
          messages: [
            {
              message: 'Command_Center',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Command_Center',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '89wja0hrqis0': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum-sport',
          ],
          messages: [
            {
              message: 'Ambient_Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '4dqov71oe70l': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum-sport',
          ],
          messages: [
            {
              message: 'Entertainment_System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_ Entertainment_System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '1zfbkmer88eh': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum-sport',
          ],
          messages: [
            {
              message: 'Super_Cruise',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Super_Cruise',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        vw7ix18goypx: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum-sport',
          ],
          messages: [
            {
              message: 'Headup_Display',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Headup_Display',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ejzd2tssw6fp: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'v-series',
          ],
          messages: [
            {
              message: 'Exterior_Design',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        up00tngp5v1o: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'v-series',
          ],
          messages: [
            {
              message: 'SHADER_Front_grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front_Grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        habfgb6fu2k2: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'v-series',
          ],
          messages: [
            {
              message: 'SHADER_Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        bk25r28rt6y0: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'v-series',
          ],
          messages: [
            {
              message: 'Rear_Fascia',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ljxlvsxjaks3: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'v-series',
          ],
          messages: [
            {
              message: 'Power_Doors',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        pxffbvox76hj: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'v-series',
          ],
          messages: [
            {
              message: 'SHADER_Infotainment_screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment_Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ahgeo58xhzrf: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'v-series',
          ],
          messages: [
            {
              message: 'SHADER_Sound_System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Sound_System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          // See knb6d6ov609e (Sport)'s comment - DOORS_CLOSE was racing the
          // shader in the same non-awaited message batch. Moved to
          // affectedInteractionOnApply, which is awaited before `messages`
          // starts, so the door finishes closing first.
          affectedInteractionOnApply: {
            p6oclm23ki5e: { add: false, snapToLastFrame: true },
          },
        },
        ja3opivostgzo4: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'v-series',
          ],
          messages: [
            {
              message: 'Steering_Wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Steering_Wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        jhzoo1cys8wj: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'v-series',
          ],
          messages: [
            {
              message: 'Command_Center',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Command_Center',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        vv7soximywgs: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'v-series',
          ],
          messages: [
            {
              message: 'Ambient_Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '4d2gtev6hchw': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'v-series',
          ],
          messages: [
            {
              message: 'Entertainment_System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_ Entertainment_System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        vep0tetjgaz6: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'v-series',
          ],
          messages: [
            {
              message: 'Super_Cruise',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Super_Cruise',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ya2qkgvmvel8: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'v-series',
          ],
          messages: [
            {
              message: 'Headup_Display',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Headup_Display',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
      },
      accessories: {
        gdnmye26fu4u: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'INT_DECO_1_Luxury',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6418) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'INT_DECO_1_Luxury',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6415) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'INT_DECO_3_Luxury/Sport',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6607) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8ylilfy10w5s': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variant_Parts_Luxury',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8yrlilfy10w5s': {
          view: 'exterior',
          groupId: 'luxury-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Touring_Package',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8yrllilfy10w5s': {
          view: 'exterior',
          groupId: 'luxury-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_38_SPEAKER_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6418) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'WO_38_SPEAKER_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6415) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'WO_38_SPEAKER_Brandy',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6411) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8yrplilfy10w5s': {
          view: 'exterior',
          groupId: 'luxury-g2',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Captain_Jet_Black_with_Perforated_inserts',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6418) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'Captain_Brandy_with_Very_Dark_Atmosphere_accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6415) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Captain_Renaissance_Red_with_Jet_Black_accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6411) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8yrkolilfy10w5s': {
          view: 'exterior',
          groupId: 'luxury-g3',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'R4E_22in',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8yrkolillpfy10w5s': {
          view: 'exterior',
          groupId: 'luxury-g3',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'RF7 24in',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ALLOY',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Wheels',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8yrplinhllfy10w5s': {
          view: 'interior',
          groupId: 'luxury-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bench_Jet_Black_with_Perforated_inserts',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6418) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'Bench_Brandy_with_Very_Dark_Atmosphere_accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6415) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Bench_Renaissance_Red_with_Jet_Black_accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6411) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8yrkolklpillpfy10w5s': {
          view: 'interior',
          groupId: 'luxury-g0',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Touring_Package',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8rtrkolklpillpfy10w5s': {
          view: 'interior',
          groupId: 'luxury-g1',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_38_SPEAKER_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6418) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'W_38_SPEAKER_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6415) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'W_38_SPEAKER_Brandy',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6411) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '90rrkokfhfbpillpfy10w5s': {
          view: 'exterior',
          groupId: 'luxury-g4',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_NIGHT_VISION',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8rtrkokfhfbpillpfy10w5s': {
          view: 'exterior',
          groupId: 'luxury-g4',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_NIGHT_VISION',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8rtasdfgbpillpfy10w5s': {
          view: 'exterior',
          groupId: 'luxury-g5',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'CONSOLE_REAR_1_WO_HDMI',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8rklpoiujbpillpfy10w5s': {
          view: 'interior',
          groupId: 'luxury-g5',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'CONSOLE_REAR_1_W_HDMI',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6418) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'Rear_Seat_Entertainment_System_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6418) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'CONSOLE_REAR_1_W_HDMI',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6415) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Rear_Seat_Entertainment_System_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6515) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'CONSOLE_REAR_1_W_HDMI',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6411) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Rear_Seat_Entertainment_System_Brandy',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6411) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        zu5m5sb9t81m: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'INT_DECO_2_Sport',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6418) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'INT_DECO_2_Sport',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6427) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'INT_DECO_3_Luxury/Sport',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6607) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        k0h4maktx6fu: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variant_Parts_Sport',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        jusz70vfg3wf: {
          view: 'exterior',
          groupId: 'sport-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Touring_Package',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        of42f2v2yhk4: {
          view: 'exterior',
          groupId: 'sport-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_38_SPEAKER_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '12a5c5qeaf6c': {
          view: 'exterior',
          groupId: 'sport-g2',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Captain_Jet_Black_with_Perforated_inserts',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6418) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'Captain_Whisper_Beige_with_Jet_Black_accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6427) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Captain_Renaissance_Red_with_Jet_Black_accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6411) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        rq4hxnft62j7: {
          view: 'exterior',
          groupId: 'sport-g3',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'R4F 22in',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        dncn7hz4g6kt: {
          view: 'exterior',
          groupId: 'sport-g3',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'RF5 24in',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ALLOY',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Wheels',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8yrkopolillpfy10w5s': {
          view: 'exterior',
          groupId: 'sport-g3',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'Onyx_Package',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ONYX_PACKAGE',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ONYX_PACKAGE',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '6yw6n1ws9p68': {
          view: 'interior',
          groupId: 'sport-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bench_Jet_Black_with_Perforated_inserts',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6418) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'Bench_Whisper_Beige_with_Jet_Black_accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6427) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Bench_Renaissance_Red_with_Jet_Black_accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6411) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '4sls1qj6n5ck': {
          view: 'interior',
          groupId: 'sport-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_38_SPEAKER_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        tg03zsnqhgwn: {
          view: 'exterior',
          groupId: 'sport-g4',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_NIGHT_VISION',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        f6fvegzexe88: {
          view: 'interior',
          groupId: 'sport-g0',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Touring_Package',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        xuxf5pk3xwud: {
          view: 'exterior',
          groupId: 'sport-g4',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_NIGHT_VISION',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        wn6eoge8afkm: {
          view: 'exterior',
          groupId: 'sport-g5',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'CONSOLE_REAR_1_WO_HDMI',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        pysqjeg9tgf8: {
          view: 'interior',
          groupId: 'sport-g5',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'CONSOLE_REAR_1_W_HDMI',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Rear_Seat_Entertainment_System_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        btyd2soc1zlw: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'INT_DECO_4_PLT_Lux/PLT_Sport',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6405) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'Rear_Seat_Entertainment_System_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6405) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'W_38_SPEAKER_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6405) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'INT_DECO_5_PLT_Lux/PLT_Sport',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6419) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Rear_Seat_Entertainment_System_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6419) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'W_38_SPEAKER_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6419) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'INT_DECO_5_PLT_Lux/PLT_Sport',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6404) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Rear_Seat_Entertainment_System_Whisper_Gideon',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6404) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'W_38_SPEAKER_Whisper_Gideon',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6404) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        nxnxvn7d8c3r: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variant_Parts_Platinum_Luxury',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        qj6dxatn057y: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'CONSOLE_REAR_1_W_HDMI',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '51ruk1agnbdv': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'W_NIGHT_VISION',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        lrhbknsfx974: {
          view: 'exterior',
          groupId: 'platinum-luxury-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Captain_Jet_Black_with_Mondrian_Quilting',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6405) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'Captain_Sheer_Gray_with_Jet_Black_accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6419) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Captain_Whisper Beige with Gideon accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6404) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ndt94xrjumar: {
          view: 'exterior',
          groupId: 'platinum-luxury-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'R4D 22in',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        f8lunm5xjxo4: {
          view: 'exterior',
          groupId: 'platinum-luxury-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'RF7 24in',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ALLOY',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Wheels',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        gfd5nv4niood: {
          view: 'interior',
          groupId: 'platinum-luxury-g0',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Executive_Jet_Black_with_Mondrian_Quilting',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6405) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'W_Executive_Sheer_Gray_with_Jet_Black_accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6419) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'W_Executive_Whisper Beige with Gideon accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6404) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ce6nckeukfx3: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'INT_DECO_4_PLT_Lux/PLT_Sport',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6405) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'Rear_Seat_Entertainment_System_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6405) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'W_38_SPEAKER_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6405) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'INT_DECO_5_PLT_Lux/PLT_Sport',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6419) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Rear_Seat_Entertainment_System_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6419) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'W_38_SPEAKER_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6419) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'INT_DECO_5_PLT_Lux/PLT_Sport',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6404) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Rear_Seat_Entertainment_System_Whisper_Gideon',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6404) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'W_38_SPEAKER_Whisper_Gideon',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6404) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        dd3ht5t32etq: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variant_Parts_Platinum_Sport',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        c8xqfoz981wp: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'CONSOLE_REAR_1_W_HDMI',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        lypvdd7yyj0l: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'W_NIGHT_VISION',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        w2tf0r4a2knz: {
          view: 'exterior',
          groupId: 'platinum-sport-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Captain_Jet_Black_with_Mondrian_Quilting',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6405) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'Captain_Sheer_Gray_with_Jet_Black_accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6419) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Captain_Whisper Beige with Gideon accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6404) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        z837kwy9wls0: {
          view: 'exterior',
          groupId: 'platinum-sport-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'SMM 22in',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        i4dnswxc0d90: {
          view: 'exterior',
          groupId: 'platinum-sport-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'RF5 24in',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ALLOY',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Wheels',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8glefqypdy46': {
          view: 'exterior',
          groupId: 'platinum-sport-g1',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'Onyx_Package',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ONYX_PACKAGE',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ONYX_PACKAGE',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        fiu9a1c8ir5n: {
          view: 'interior',
          groupId: 'platinum-sport-g0',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Executive_Jet_Black_with_Mondrian_Quilting',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6405) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'W_Executive_Sheer_Gray_with_Jet_Black_accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6419) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'W_Executive_Whisper Beige with Gideon accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6404) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '2gxjds87y443': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Captain_Jet_Black_with_Mondrian_Quilting',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6595) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'Captain_Sheer_Gray_with_Jet_Black_accents',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6594) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'v-series',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        uioaga6go12e: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variant_Parts_V_Series',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'v-series',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        o4wwlrl3fven: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'W_38_SPEAKER_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'v-series',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        x9dce7dvd85p: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'CONSOLE_REAR_1_W_HDMI',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'v-series',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '0s7bm9k1h7vq': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Rear_Seat_Entertainment_System_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'v-series',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        j1eat5kj0b9e: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'W_NIGHT_VISION',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'v-series',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '4v195on8wcf4': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'R5D 24in',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'v-series',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
      },
      packages: {
        '8yrkopolillpfy10w5s': {
          groupId: 'sport-g3',
          accessoryIds: [
            '8yrkopolillpfy10w5s',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            'sport',
          ],
        },
        f6fvegzexe88: {
          groupId: 'sport-g0',
          accessoryIds: [
            'f6fvegzexe88',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            'sport',
          ],
        },
        gfd5nv4niood: {
          groupId: 'platinum-luxury-g0',
          accessoryIds: [
            'gfd5nv4niood',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            'platinum-luxury',
          ],
        },
        '8glefqypdy46': {
          groupId: 'platinum-sport-g1',
          accessoryIds: [
            '8glefqypdy46',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            'platinum-sport',
          ],
        },
        fiu9a1c8ir5n: {
          groupId: 'platinum-sport-g0',
          accessoryIds: [
            'fiu9a1c8ir5n',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            'platinum-sport',
          ],
        },
      },
      interactions: {
        ihmz2ch7lru2: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        '53ehrrkf0yxy': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        rc1z7dwmbfif: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        w3yjdzjao2i0: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        toijdriu1av5: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        g64qit5yzfp2: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        '8xaasoxlyubu': {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sport',
          ],
        },
        wkva8yld7j1a: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sport',
          ],
        },
        pk3dhqpkibjd: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sport',
          ],
        },
        '7xkjflfgf38q': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sport',
          ],
        },
        ppv2p9fprbyu: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sport',
          ],
        },
        yhn65g05fv3l: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sport',
          ],
        },
        '6gohvikpcnsq': {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum-luxury',
          ],
        },
        j33lsupbq8po: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum-luxury',
          ],
        },
        '9a7dr6gbgr8l': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum-luxury',
          ],
        },
        '0hweglhy9gu0': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum-luxury',
          ],
        },
        vr8mkba7vtqr: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum-luxury',
          ],
        },
        bl1x9jj8jecr: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum-luxury',
          ],
        },
        sl5w76z5a23v: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum-sport',
          ],
        },
        l54bmp5vf4am: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum-sport',
          ],
        },
        '298irexzk84w': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum-sport',
          ],
        },
        wh1frmtr0u28: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum-sport',
          ],
        },
        q9ddyvv56xva: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum-sport',
          ],
        },
        ahiduitywbma: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum-sport',
          ],
        },
        p6oclm23ki5e: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'v-series',
          ],
        },
        '54kqwzjme0so': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'v-series',
          ],
        },
        '7xfwfti9azof': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'v-series',
          ],
        },
        gj4u0vlgz20o: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'v-series',
          ],
        },
        '7hs9183l5tlb': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'v-series',
          ],
        },
        af0dl2x747nl: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'v-series',
          ],
        },
      },
      hotspots: {
        exteriorCamera: {
          type: 'exterior',
          messages: {
            fov: 0.4,
            alpha: 2.02,
            beta: 1.47,
            radius: 10,
            lowerAlphaLimit: null,
            upperAlphaLimit: null,
            lowerBetaLimit: 0.1,
            upperBetaLimit: 1.6,
            lowerRadiusLimit: 8,
            upperRadiusLimit: 11,
            target: {
              x: 0,
              y: 0.65,
              z: 0,
            },
            isFovZoomModeOn: false,
          },
        },
        interiorCamera: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_1: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_2',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_2: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_3',
            identifiers: [
              'common_exterior',
            ],
          },
        },
      },
      usdzPathWithVariable: '',
    },
  },
};
